import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Settings, Download, Share2, CheckCircle, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import CertificateEditor from "@/components/CertificateEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

const Verify = () => {
  const { serial } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState(null);
  const [error, setError] = useState(null);
  const [isIssuer, setIsIssuer] = useState(false);

  useEffect(() => {
    fetchCertificate();
    checkIssuerStatus();
  }, [serial]);

  const checkIssuerStatus = () => {
    // Check if user is logged in as an issuer
    const token = localStorage.getItem('issuer_token');
    if (token) {
      setIsIssuer(true);
    }
  };

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API}/verify/${serial}`);
      setData(response.data);
      
      // Fetch template data if template_id exists
      if (response.data.certificate.template_id) {
        try {
          const templateResponse = await axios.get(`${API}/templates/${response.data.certificate.template_id}`);
          setTemplate(templateResponse.data);
        } catch (templateErr) {
          console.error("Failed to fetch template:", templateErr);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Certificate not found");
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = () => {
    window.open(`${API}/download/${serial}/pdf`, '_blank');
    toast.success("Downloading PDF...");
  };

  const downloadPNG = () => {
    window.open(`${API}/download/${serial}/png`, '_blank');
    toast.success("Downloading PNG...");
  };

  const shareLinkedIn = async () => {
    await axios.post(`${API}/track/${serial}/linkedin-share`);
    // Use backend OG endpoint with cache-busting parameter
    const cacheBuster = Date.now();
    const url = encodeURIComponent(`${BACKEND_URL}/api/og/verify/${serial}?v=${cacheBuster}`);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
    toast.success("Opening LinkedIn...");
  };

  const addToLinkedInProfile = async () => {
    // LinkedIn's Add to Profile URL for certifications
    const certUrl = encodeURIComponent(`${FRONTEND_URL}/verify/${serial}`);
    const certName = encodeURIComponent(data.certificate.title);
    const orgName = encodeURIComponent(data.issuer.organization_name);
    
    // Parse issue date properly
    const issueDate = new Date(data.recipient.issued_at);
    const issueYear = issueDate.getFullYear();
    const issueMonth = issueDate.getMonth() + 1; // getMonth() returns 0-11, LinkedIn needs 1-12
    
    // LinkedIn Add Certification URL - pre-fills all fields
    const linkedInUrl = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${certName}&organizationName=${orgName}&issueYear=${issueYear}&issueMonth=${issueMonth}&certUrl=${certUrl}&certId=${serial}`;
    
    window.open(linkedInUrl, '_blank');
    toast.success("✨ LinkedIn will open with pre-filled form. Just click 'Save' to add to your profile and trigger celebration!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="verify-loading">
        <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="verify-error">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Certificate Not Found</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate("/")} data-testid="go-home-btn">Go Home</Button>
        </div>
      </div>
    );
  }

  const { recipient, certificate, issuer } = data;
  const isRevoked = recipient.revoked;
  
  // Open Graph meta data
  const certificateUrl = `${FRONTEND_URL}/verify/${serial}`;
  const certificateImageUrl = `${API}/download/${serial}/png`;
  const ogTitle = `${recipient.name} - ${certificate.title}`;
  const ogDescription = `Certificate issued by ${issuer.organization_name} for ${certificate.course_name}. Verify authenticity with serial number ${serial}.`;

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>{ogTitle} | Certificate Machine</title>
        <meta name="description" content={ogDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={certificateUrl} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={certificateImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Certificate Machine" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={certificateUrl} />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={certificateImageUrl} />
        
        {/* LinkedIn specific */}
        <meta property="og:image:secure_url" content={certificateImageUrl} />
      </Helmet>
      
      {/* Navigation */}
      <nav className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50" data-testid="verify-nav">
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-2" onClick={() => navigate("/")} style={{cursor: 'pointer'}}>
            <Settings className="h-8 w-8 text-slate-900" />
            <span className="text-xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Certificate Machine</span>
          </div>
        </div>
      </nav>

      {/* Verification Content */}
      <div className="max-w-4xl mx-auto px-6 py-16" data-testid="verify-content">
        {/* Back to Dashboard Button for Issuers */}
        {isIssuer && (
          <button
            onClick={() => navigate('/issuer/dashboard')}
            className="mb-6 flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors rounded-lg hover:bg-white"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back to Dashboard</span>
          </button>
        )}
        {isRevoked && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8" data-testid="revoked-banner">
            <div className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              <span className="font-medium">This credential has been revoked</span>
            </div>
          </div>
        )}

        {/* Certificate Display */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-lg mb-8" data-testid="certificate-display">
          {template ? (
            <div className="w-full flex items-center justify-center">
              <CertificateEditor
                templateBackground={template.background_url ? `${API}/proxy-image?url=${encodeURIComponent(template.background_url)}` : null}
                onSave={() => {}}
                logoPreview={certificate.logo_base64 || null}
                instructorName={certificate.instructor_name || issuer.organization_name}
                courseName={certificate.course_name || certificate.title}
                recipientName={recipient.name}
                completionDate={recipient.completion_date}
                serialNumber={recipient.serial_number}
                initialLayout={certificate.template_layout || template.layout}
                isCustomBackground={true}
                readOnly={true}
              />
            </div>
          ) : (
            <div className="text-center p-12">
              <h1 className="text-4xl md:text-5xl font-medium text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>Certificate of Achievement</h1>
              <p className="text-sm text-slate-500 mb-8">This certificate is proudly presented to</p>
              
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-8" style={{fontFamily: 'Playfair Display'}}>{recipient.name}</h2>
              
              <p className="text-slate-600 mb-2">For successful completion of</p>
              <h3 className="text-2xl font-semibold text-slate-900 mb-12" style={{fontFamily: 'Playfair Display'}}>{certificate.title}</h3>
              
              <div className="flex justify-between items-end text-sm text-slate-600">
                <div>
                  <p>Date: {recipient.completion_date}</p>
                  <p className="serial-number">Serial: {recipient.serial_number}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-slate-900">{issuer.organization_name}</p>
                  <p>Authorized Issuer</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Verification Badge */}
        {!isRevoked && (
          <div className="flex items-center justify-center gap-2 mb-8" data-testid="verified-badge">
            <CheckCircle className="h-6 w-6 text-green-600" />
            <span className="text-lg font-medium text-green-600">Verified Credential</span>
          </div>
        )}

        {/* Action Buttons */}
        {!isRevoked && (
          <div className="flex flex-wrap gap-4 justify-center" data-testid="action-buttons">
            <Button onClick={downloadPDF} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-6 py-3" data-testid="download-pdf-btn">
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            <Button onClick={downloadPNG} variant="outline" className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg px-6 py-3" data-testid="download-png-btn">
              <Download className="h-4 w-4 mr-2" />
              Download PNG
            </Button>
            <Button onClick={shareLinkedIn} variant="outline" className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg px-6 py-3" data-testid="share-linkedin-btn">
              <Share2 className="h-4 w-4 mr-2" />
              Share Post
            </Button>
            <Button onClick={addToLinkedInProfile} className="bg-[#0A66C2] text-white hover:bg-[#004182] rounded-lg px-6 py-3" data-testid="add-linkedin-cert-btn">
              <Share2 className="h-4 w-4 mr-2" />
              Add to LinkedIn Profile
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Verify;
